import { LogCategory } from '@soteria/logger/browser';
import type { Middleware, AnyAction } from 'redux';

import { logger } from './index';

/**
 * Configuration for Redux logging middleware
 */
export interface ReduxLoggerConfig {
  // Actions to skip logging (high-frequency or noisy actions)
  skipActions?: string[];
  // Actions to always log (important actions)
  forceLogActions?: string[];
  // State paths to include in state change logging
  loggedStatePaths?: string[];
  // Performance threshold for slow action detection (ms)
  slowActionThreshold?: number;
  // Whether to log action payloads
  logPayloads?: boolean;
  // Whether to log state changes
  logStateChanges?: boolean;
}

/**
 * Default configuration for Redux logging
 */
const defaultConfig: ReduxLoggerConfig = {
  skipActions: [
    // High-frequency UI state changes
    'core/theme/SET_THEME_LOADING',
    'core/settingsDialog/SET_OPEN',
    // Redux persist actions (already handled by persist middleware)
    'persist/FLUSH',
    'persist/REHYDRATE',
    'persist/PAUSE',
    'persist/PERSIST',
    'persist/PURGE',
    'persist/REGISTER',
  ],
  forceLogActions: [
    // Authentication business events only
    'auth/login',
    'auth/loginSuccess',
    'auth/loginFailure',
    'auth/logout',
    'auth/providerSelected',
    'auth/sessionRefresh',
    // Business conversion events
    'communities/conversionEvent',
    'safetyForge/featureAdoption',
  ],
  loggedStatePaths: ['auth.isAuthenticated', 'auth.user.id'],
  slowActionThreshold: 100,
  logPayloads: true,
  logStateChanges: true,
};

/**
 * Helper function to sanitize payload (remove sensitive data)
 */
function sanitizePayload(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return payload;

  const sensitiveFields = ['password', 'token', 'secret', 'authorization', 'accessToken', 'refreshToken'];
  const sanitized = { ...(payload as Record<string, unknown>) };

  for (const key of Object.keys(sanitized)) {
    if (sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizePayload(sanitized[key]);
    }
  }

  return sanitized;
}

/**
 * Helper function to determine if an action should be logged
 */
function shouldLogAction(actionType: string, config: ReduxLoggerConfig): boolean {
  // Check if action is in skip list
  if (config.skipActions?.some(skipPattern => actionType.includes(skipPattern))) {
    return false;
  }

  // Check if action is in force log list
  if (config.forceLogActions?.some(forcePattern => actionType.includes(forcePattern))) {
    return true;
  }

  // Default to logging most actions except those explicitly skipped
  return true;
}

/**
 * Helper function to extract relevant state changes
 */
function getStateChanges(prevState: unknown, nextState: unknown, loggedPaths: string[]): Record<string, { from: unknown; to: unknown }> {
  const changes: Record<string, { from: unknown; to: unknown }> = {};

  for (const path of loggedPaths) {
    const pathParts = path.split('.');
    let prevValue = prevState;
    let nextValue = nextState;

    // Navigate to the nested property
    for (const part of pathParts) {
      prevValue = (prevValue as Record<string, unknown>)?.[part];
      nextValue = (nextValue as Record<string, unknown>)?.[part];
    }

    // Only log if there's an actual change
    if (prevValue !== nextValue) {
      changes[path] = {
        from: prevValue,
        to: nextValue,
      };
    }
  }

  return changes;
}

/**
 * Helper function to log Redux action
 * Simplified to focus on business events without correlation ID tracking
 */
function logReduxAction(action: AnyAction, config: ReduxLoggerConfig): void {
  const isErrorAction = action.type.includes('rejected') || action.type.includes('FAILURE') || action.type.includes('ERROR');

  // Log the action - use TRACE level for Redux actions to reduce console noise
  const logLevel = isErrorAction ? 'error' : 'trace';
  const actionContext = {
    category: LogCategory.SYSTEM,
    action: {
      type: action.type,
      ...(config.logPayloads && action.payload ? { payload: sanitizePayload(action.payload) } : {}),
    },
    redux: {
      actionType: action.type,
      timestamp: new Date().toISOString(),
    },
  };

  logger[logLevel](`Redux action: ${action.type}`, actionContext);
}

/**
 * Helper function to log Redux errors
 */
function logReduxError(action: AnyAction): void {
  if (action.payload && action.error) {
    const errorPayload = action.payload;

    logger.error(`Redux error action: ${action.type}`, {
      category: LogCategory.ERROR,
      actionType: action.type,
      error:
        errorPayload instanceof Error
          ? {
              name: errorPayload.name,
              message: errorPayload.message,
              stack: errorPayload.stack,
            }
          : errorPayload,
      redux: {
        actionType: action.type,
        isRejectedAction: true,
      },
    });
  }
}

/**
 * Helper function to log slow actions
 */
function logSlowAction(action: AnyAction, duration: number, threshold: number): void {
  logger.warn(`Slow Redux action detected: ${action.type}`, {
    category: LogCategory.PERF,
    actionType: action.type,
    duration,
    performance: {
      actionDuration: duration,
      threshold,
      isSlowAction: true,
    },
  });
}

/**
 * Helper function to log state changes
 */
function logStateChanges(action: AnyAction, stateChangeData: { prevState: unknown; nextState: unknown; loggedPaths: string[] }): void {
  const stateChanges = getStateChanges(stateChangeData.prevState, stateChangeData.nextState, stateChangeData.loggedPaths);

  if (Object.keys(stateChanges).length > 0) {
    logger.trace(`Redux state changes from ${action.type}`, {
      category: LogCategory.SYSTEM,
      actionType: action.type,
      stateChanges,
      redux: {
        hasStateChanges: true,
        changedPaths: Object.keys(stateChanges),
      },
    });
  }
}

/**
 * Create Redux logger middleware with configuration
 */
export const createReduxLoggerMiddleware = (config: Partial<ReduxLoggerConfig> = {}): Middleware => {
  const finalConfig = { ...defaultConfig, ...config };

  return store => next => (action: unknown) => {
    const typedAction = action as AnyAction;
    const startTime = performance.now();
    const prevState = store.getState();

    // Skip logging if action should not be logged
    if (!shouldLogAction(typedAction.type, finalConfig)) {
      return next(action);
    }

    // Log the action
    logReduxAction(typedAction, finalConfig);

    // For error actions, log additional error details
    const isErrorAction = typedAction.type.includes('rejected') || typedAction.type.includes('FAILURE') || typedAction.type.includes('ERROR');

    if (isErrorAction) {
      logReduxError(typedAction);
    }

    // Execute the action
    const result = next(action);
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Log performance if action was slow
    const threshold = finalConfig.slowActionThreshold || 100;

    if (duration > threshold) {
      logSlowAction(typedAction, duration, threshold);
    }

    // Log state changes if enabled
    if (finalConfig.logStateChanges && finalConfig.loggedStatePaths) {
      const nextState = store.getState();

      logStateChanges(typedAction, {
        prevState,
        nextState,
        loggedPaths: finalConfig.loggedStatePaths,
      });
    }

    return result;
  };
};

/**
 * Default Redux logger middleware instance
 */
export const reduxLoggerMiddleware = createReduxLoggerMiddleware();
